<template></template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, nextTick } from 'vue';
import type { IntegrationPixelTrackingModel, PixelItemState } from './Model';
import { EventType, TrackByType, TriggerType } from './Model';
import useDevice from '@/src/hooks/useDevice';
import { CookieCategory, waitForCookieAccess } from '@/src/services/cookieConsent';
import { events } from '@/src/services/events';
import { getQueryParams } from '@/src/utilities/Url';
import { applyReplacementTags } from '@/src/utilities/Utilities';
import { useCampaignStore } from '@/src/store/campaign';
import { loadFBSdk } from '@/src/components/integrations/pixel-tracking/sdk/Facebook';
import { loadTikTokSdk } from '@/src/components/integrations/pixel-tracking/sdk/TikTok';

export default defineComponent({
  name: 'integration-pixel-tracking',
  props: {
    model: {
      type: Object as PropType<IntegrationPixelTrackingModel>,
      required: true
    }
  },
  setup(props) {
    const state = (props.model as IntegrationPixelTrackingModel).state;
    const firedPixels: string[] = [];
    const campaignStore = useCampaignStore();

    const executePixelCode = (code: string) => {
      // Create a temporary div to use the browser's HTML parser
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = code;

      // Handle <script> tags
      const scripts = tempDiv.querySelectorAll('script');
      scripts.forEach((script) => {
        const newScript = document.createElement('script');

        // Copy attributes if the script is external
        if (script.src) {
          Array.from(script.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });

          // Boolean attributes need explicit handling
          if (script.hasAttribute('async')) newScript.async = true;
          if (script.hasAttribute('defer')) newScript.defer = true;
          if (script.hasAttribute('nomodule')) newScript.noModule = true;
        } else {
          // For inline scripts, copy the text content
          newScript.textContent = script.textContent;
        }

        // Append the new script to the document head
        document.head.appendChild(newScript);
      });

      // Handle <img> tags for image pixels
      const imgs = tempDiv.querySelectorAll('img');
      imgs.forEach((img) => {
        // Check if an image with the same src already exists
        if (!document.querySelector(`img[src="${img.src}"]`)) {
          const newImg = new Image();
          Array.from(img.attributes).forEach((attr) => {
            newImg.setAttribute(attr.name, attr.value);
          });
          newImg.style.display = 'none';
          // Append the new image to the document body
          document.body.appendChild(newImg);
        }
      });
    };

    const applyPixelTracking = async (pixel: PixelItemState) => {
      // Disable pixels getting fired when editing mode is active
      if (campaignStore.model?.state.isEditModeActive) {
        return;
      }

      const { isMobile, isDesktop, isTablet } = useDevice();

      if (
        (pixel.devices.mobile && isMobile) ||
        (pixel.devices.desktop && isTablet) ||
        (pixel.devices.desktop && isDesktop)
      ) {
        if (pixel.visibilityCondition?.check()) {
          // Pixels shouldn't be fired more than once
          if (firedPixels.includes(pixel.id)) {
            return;
          }

          firedPixels.push(pixel.id);

          await waitForCookieAccess(CookieCategory.MARKETING);

          if (pixel.trackBy === TrackByType.CODE) {
            let code = window.atob(pixel.code);
            code = applyReplacementTags(code);

            const queryParams = getQueryParams();

            if (queryParams) {
              for (const parameter in queryParams) {
                // eslint-disable-next-line security/detect-non-literal-regexp
                code = code.replace(new RegExp('#' + String(parameter) + '#', 'ig'), queryParams[`${parameter}`]);
              }
            }

            executePixelCode(code);
          }

          if (pixel.trackBy === TrackByType.FACEBOOK_ID) {
            if (loadFBSdk() && window.fbq) {
              const pixelId = window.atob(pixel.code);
              window.fbq('init', `${pixelId}`);
            }

            const eventType = pixel.eventType === EventType.CUSTOM_EVENT ? 'trackCustom' : 'track';
            const eventName = pixel.eventName || 'PageView';

            if (typeof window.fbq !== 'undefined') {
              window.fbq(`${eventType}`, `${eventName}`);
            }
          }

          if (pixel.trackBy === TrackByType.TIKTOK_ID) {
            if (loadTikTokSdk() && window.ttq) {
              const pixelId = window.atob(pixel.code);
              window.ttq.load(pixelId);
            }

            if (typeof window.ttq !== 'undefined') {
              const eventMapping: { [key: number]: () => void } = {
                [EventType.PAGE_VIEW]: () => window.ttq?.page(),
                [EventType.CLICK_BUTTON]: () => window.ttq?.track('ClickButton'),
                [EventType.COMPLETE_REGISTRATION]: () => window.ttq?.track('CompleteRegistration'),
                [EventType.INITIATE_CHECKOUT]: () => window.ttq?.track('InitiateCheckout'),
                [EventType.SUBMIT_FORM]: () => window.ttq?.track('SubmitForm'),
                [EventType.SUBSCRIBE]: () => window.ttq?.track('Subscribe')
              };

              if (pixel.eventType !== undefined) {
                const trackEvent = eventMapping[pixel.eventType];
                if (trackEvent) {
                  trackEvent();
                }
              }
            }
          }
        }
      }
    };

    const onInit = async () => {
      if (state.pixels && state.pixels?.length > 0) {
        state.pixels.forEach(async (pixel) => {
          if (pixel.trigger === TriggerType.PAGELOAD) {
            applyPixelTracking(pixel);
          }
        });
      }
    };

    events.on('flowPageChange', async () => {
      if (state.pixels && state.pixels?.length > 0) {
        state.pixels.forEach(async (pixel) => {
          if (pixel.trigger === TriggerType.FLOWPAGE) {
            await nextTick();
            applyPixelTracking(pixel);
            // needs to wait for vue to update flowPage id to the next
          }
        });
      }
    });

    events.on('formSubmit', async (e) => {
      if (state.pixels && state.pixels?.length > 0) {
        state.pixels.forEach(async (pixel) => {
          if (pixel.trigger === TriggerType.CONFIRM && e.detail.id === pixel.confirmSectionId) {
            await nextTick();
            applyPixelTracking(pixel);
          }
        });
      }
    });

    onInit();
  }
});
</script>
